import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { useGoogleMaps } from '../../contexts/GoogleMapsContext';

const Map = ({ serviceLocation, fuelLocation, dropLocation, vendors, selectedVendorId, onVendorSelect, onRouteCalculated, isTowingService, hasFuelLocation }) => {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const outboundRouteRef = useRef(null);
  const serviceRouteRef = useRef(null);
  const returnRouteRef = useRef(null);
  const dropLocationMarkerRef = useRef(null);
  const fuelLocationMarkerRef = useRef(null);
  const { isLoaded: googleMapsLoaded, loading: googleMapsLoading, error: googleMapsError } = useGoogleMaps();

  // Memoize locations by primitive fields to avoid unnecessary re-renders
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedServiceLocation = useMemo(() => serviceLocation, [
    serviceLocation?.latitude,
    serviceLocation?.longitude,
    serviceLocation?.address,
    serviceLocation?.city,
    serviceLocation?.state
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedFuelLocation = useMemo(() => fuelLocation, [
    fuelLocation?.latitude,
    fuelLocation?.longitude,
    fuelLocation?.address,
    fuelLocation?.city,
    fuelLocation?.state
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedDropLocation = useMemo(() => dropLocation, [
    dropLocation?.latitude,
    dropLocation?.longitude,
    dropLocation?.address,
    dropLocation?.city,
    dropLocation?.state
  ]);

  // Memoize vendors to prevent unnecessary re-renders
  const memoizedVendors = useMemo(() => vendors, [vendors]);

  // Memoized route calculation function
  const calculateRoutes = useCallback(() => {
    if (!googleMapsLoaded || !mapInstanceRef.current || !memoizedServiceLocation?.latitude || !memoizedServiceLocation?.longitude) {
      return;
    }
    
    // Clear existing routes
    if (outboundRouteRef.current) {
      outboundRouteRef.current.setMap(null);
      outboundRouteRef.current = null;
    }
    if (serviceRouteRef.current) {
      serviceRouteRef.current.setMap(null);
      serviceRouteRef.current = null;
    }
    if (returnRouteRef.current) {
      returnRouteRef.current.setMap(null);
      returnRouteRef.current = null;
    }
    
    // If no vendor selected, don't draw routes
    if (!selectedVendorId) {
      return;
    }
    
    // Find selected vendor
    const selectedVendorData = memoizedVendors.find(v => v.location.id === selectedVendorId);
    if (!selectedVendorData || !selectedVendorData.coordinates?.latitude || !selectedVendorData.coordinates?.longitude) return;

    const selectedVendor = selectedVendorData;
    const directionsService = new window.google.maps.DirectionsService();
    let outboundDistance = 0;
    let serviceDistance = 0;
    let returnDistance = 0;
    let outboundDuration = 0;
    let serviceDuration = 0;

    if (isTowingService && memoizedDropLocation?.latitude && memoizedDropLocation?.longitude) {
      // Four-point routing for towing services with fuel: Vendor → Fuel → Pickup → Drop → Vendor
      // or Three-point routing for towing services without fuel: Vendor → Pickup → Drop → Vendor
      
      if (hasFuelLocation && memoizedFuelLocation?.latitude && memoizedFuelLocation?.longitude) {
        // Four-point routing: Vendor → Fuel → Pickup → Drop → Vendor
        
        // 1. Outbound route (vendor to fuel location)
        const outboundRenderer = new window.google.maps.DirectionsRenderer({
          map: mapInstanceRef.current,
          suppressMarkers: true,
          polylineOptions: {
            strokeColor: '#4CAF50', // Green for outbound
            strokeWeight: 5,
            strokeOpacity: 0.7
          }
        });

        directionsService.route(
          {
            origin: { lat: selectedVendor.coordinates.latitude, lng: selectedVendor.coordinates.longitude },
            destination: { lat: memoizedFuelLocation.latitude, lng: memoizedFuelLocation.longitude },
            travelMode: window.google.maps.TravelMode.DRIVING
          },
          (outboundResult, outboundStatus) => {
            if (outboundStatus === 'OK') {
              outboundRenderer.setDirections(outboundResult);
              outboundRouteRef.current = outboundRenderer;
              
              const outboundRoute = outboundResult.routes[0];
              outboundDistance = outboundRoute.legs[0].distance.value / 1000;
              outboundDuration = outboundRoute.legs[0].duration.value / 60;

              // 2. Fuel to pickup route
              const fuelToPickupRenderer = new window.google.maps.DirectionsRenderer({
                map: mapInstanceRef.current,
                suppressMarkers: true,
                polylineOptions: {
                  strokeColor: '#FFC107', // Yellow for fuel to pickup
                  strokeWeight: 5,
                  strokeOpacity: 0.7
                }
              });

              directionsService.route(
                {
                  origin: { lat: memoizedFuelLocation.latitude, lng: memoizedFuelLocation.longitude },
                  destination: { lat: memoizedServiceLocation.latitude, lng: memoizedServiceLocation.longitude },
                  travelMode: window.google.maps.TravelMode.DRIVING
                },
                (fuelToPickupResult, fuelToPickupStatus) => {
                  if (fuelToPickupStatus === 'OK') {
                    fuelToPickupRenderer.setDirections(fuelToPickupResult);
                    
                    const fuelToPickupRoute = fuelToPickupResult.routes[0];
                    const fuelToPickupDistance = fuelToPickupRoute.legs[0].distance.value / 1000;
                    const fuelToPickupDuration = fuelToPickupRoute.legs[0].duration.value / 60;

                    // 3. Service route (pickup to drop location)
                    const serviceRenderer = new window.google.maps.DirectionsRenderer({
                      map: mapInstanceRef.current,
                      suppressMarkers: true,
                      polylineOptions: {
                        strokeColor: '#2196F3', // Blue for service route
                        strokeWeight: 5,
                        strokeOpacity: 0.7
                      }
                    });

                    directionsService.route(
                      {
                        origin: { lat: memoizedServiceLocation.latitude, lng: memoizedServiceLocation.longitude },
                        destination: { lat: memoizedDropLocation.latitude, lng: memoizedDropLocation.longitude },
                        travelMode: window.google.maps.TravelMode.DRIVING
                      },
                      (serviceResult, serviceStatus) => {
                        if (serviceStatus === 'OK') {
                          serviceRenderer.setDirections(serviceResult);
                          serviceRouteRef.current = serviceRenderer;
                          
                          const serviceRoute = serviceResult.routes[0];
                          serviceDistance = serviceRoute.legs[0].distance.value / 1000;
                          serviceDuration = serviceRoute.legs[0].duration.value / 60;

                          // 4. Return route (drop location to vendor)
                          const returnRenderer = new window.google.maps.DirectionsRenderer({
                            map: mapInstanceRef.current,
                            suppressMarkers: true,
                            polylineOptions: {
                              strokeColor: '#FF9800', // Orange for return
                              strokeWeight: 5,
                              strokeOpacity: 0.7
                            }
                          });

                          directionsService.route(
                            {
                              origin: { lat: memoizedDropLocation.latitude, lng: memoizedDropLocation.longitude },
                              destination: { lat: selectedVendor.coordinates.latitude, lng: selectedVendor.coordinates.longitude },
                              travelMode: window.google.maps.TravelMode.DRIVING
                            },
                            (returnResult, returnStatus) => {
                              if (returnStatus === 'OK') {
                                returnRenderer.setDirections(returnResult);
                                returnRouteRef.current = returnRenderer;
                                
                                const returnRoute = returnResult.routes[0];
                                returnDistance = returnRoute.legs[0].distance.value / 1000;

                                // Call the callback with route information
                                if (onRouteCalculated) {
                                  onRouteCalculated({
                                    vendorId: selectedVendor.location.id,
                                    outboundDistance,
                                    fuelToPickupDistance,
                                    serviceDistance,
                                    returnDistance,
                                    totalDistance: outboundDistance + fuelToPickupDistance + serviceDistance + returnDistance,
                                    outboundDuration,
                                    fuelToPickupDuration,
                                    serviceDuration,
                                    isTowingService: true,
                                    hasFuelLocation: true
                                  });
                                }
                              }
                            }
                          );
                        }
                      }
                    );
                  }
                }
              );
            }
          }
        );
      } else {
        // Three-point routing for towing services without fuel: Vendor → Pickup → Drop → Vendor
      
      // 1. Outbound route (vendor to pickup location)
      const outboundRenderer = new window.google.maps.DirectionsRenderer({
        map: mapInstanceRef.current,
        suppressMarkers: true,
        polylineOptions: {
          strokeColor: '#4CAF50', // Green for outbound
          strokeWeight: 5,
          strokeOpacity: 0.7
        }
      });

      directionsService.route(
        {
          origin: { lat: selectedVendor.coordinates.latitude, lng: selectedVendor.coordinates.longitude },
          destination: { lat: memoizedServiceLocation.latitude, lng: memoizedServiceLocation.longitude },
          travelMode: window.google.maps.TravelMode.DRIVING
        },
        (outboundResult, outboundStatus) => {
          if (outboundStatus === 'OK') {
            outboundRenderer.setDirections(outboundResult);
            outboundRouteRef.current = outboundRenderer;
            
            const outboundRoute = outboundResult.routes[0];
            outboundDistance = outboundRoute.legs[0].distance.value / 1000;
            outboundDuration = outboundRoute.legs[0].duration.value / 60;

            // 2. Service route (pickup to drop location)
            const serviceRenderer = new window.google.maps.DirectionsRenderer({
              map: mapInstanceRef.current,
              suppressMarkers: true,
              polylineOptions: {
                strokeColor: '#2196F3', // Blue for service route
                strokeWeight: 5,
                strokeOpacity: 0.7
              }
            });

            directionsService.route(
              {
                origin: { lat: memoizedServiceLocation.latitude, lng: memoizedServiceLocation.longitude },
                destination: { lat: memoizedDropLocation.latitude, lng: memoizedDropLocation.longitude },
                travelMode: window.google.maps.TravelMode.DRIVING
              },
              (serviceResult, serviceStatus) => {
                if (serviceStatus === 'OK') {
                  serviceRenderer.setDirections(serviceResult);
                  serviceRouteRef.current = serviceRenderer;
                  
                  const serviceRoute = serviceResult.routes[0];
                  serviceDistance = serviceRoute.legs[0].distance.value / 1000;
                  serviceDuration = serviceRoute.legs[0].duration.value / 60;

                  // 3. Return route (drop location to vendor)
                  const returnRenderer = new window.google.maps.DirectionsRenderer({
                    map: mapInstanceRef.current,
                    suppressMarkers: true,
                    polylineOptions: {
                      strokeColor: '#FF9800', // Orange for return
                      strokeWeight: 5,
                      strokeOpacity: 0.7
                    }
                  });

                  directionsService.route(
                    {
                      origin: { lat: memoizedDropLocation.latitude, lng: memoizedDropLocation.longitude },
                      destination: { lat: selectedVendor.coordinates.latitude, lng: selectedVendor.coordinates.longitude },
                      travelMode: window.google.maps.TravelMode.DRIVING
                    },
                    (returnResult, returnStatus) => {
                      if (returnStatus === 'OK') {
                        returnRenderer.setDirections(returnResult);
                        returnRouteRef.current = returnRenderer;
                        
                        const returnRoute = returnResult.routes[0];
                        returnDistance = returnRoute.legs[0].distance.value / 1000;

                        // Call the callback with route information
                        if (onRouteCalculated) {
                          onRouteCalculated({
                            vendorId: selectedVendor.location.id,
                            outboundDistance,
                            serviceDistance,
                            returnDistance,
                            totalDistance: outboundDistance + serviceDistance + returnDistance,
                            outboundDuration,
                            serviceDuration,
                              isTowingService: true,
                              hasFuelLocation: false
                            });
                          }
                        }
                      }
                    );
                  }
                }
              );
            }
          }
        );
      }
    } else {
      // Regular services: Vendor → Fuel → Service → Vendor (if fuel) or Vendor → Service → Vendor (if no fuel)
      
      if (hasFuelLocation && memoizedFuelLocation?.latitude && memoizedFuelLocation?.longitude) {
        // Three-point routing with fuel: Vendor → Fuel → Service → Vendor
        
        // 1. Outbound route (vendor to fuel location)
        const outboundRenderer = new window.google.maps.DirectionsRenderer({
          map: mapInstanceRef.current,
          suppressMarkers: true,
          polylineOptions: {
            strokeColor: '#4CAF50', // Green for outbound
            strokeWeight: 5,
            strokeOpacity: 0.7
          }
        });

        directionsService.route(
          {
            origin: { lat: selectedVendor.coordinates.latitude, lng: selectedVendor.coordinates.longitude },
            destination: { lat: memoizedFuelLocation.latitude, lng: memoizedFuelLocation.longitude },
            travelMode: window.google.maps.TravelMode.DRIVING
          },
          (outboundResult, outboundStatus) => {
            if (outboundStatus === 'OK') {
              outboundRenderer.setDirections(outboundResult);
              outboundRouteRef.current = outboundRenderer;
              
              const outboundRoute = outboundResult.routes[0];
              outboundDistance = outboundRoute.legs[0].distance.value / 1000;
              outboundDuration = outboundRoute.legs[0].duration.value / 60;

              // 2. Fuel to service route
              const fuelToServiceRenderer = new window.google.maps.DirectionsRenderer({
                map: mapInstanceRef.current,
                suppressMarkers: true,
                polylineOptions: {
                  strokeColor: '#FFC107', // Yellow for fuel to service
                  strokeWeight: 5,
                  strokeOpacity: 0.7
                }
              });

              directionsService.route(
                {
                  origin: { lat: memoizedFuelLocation.latitude, lng: memoizedFuelLocation.longitude },
                  destination: { lat: memoizedServiceLocation.latitude, lng: memoizedServiceLocation.longitude },
                  travelMode: window.google.maps.TravelMode.DRIVING
                },
                (fuelToServiceResult, fuelToServiceStatus) => {
                  if (fuelToServiceStatus === 'OK') {
                    fuelToServiceRenderer.setDirections(fuelToServiceResult);
                    
                    const fuelToServiceRoute = fuelToServiceResult.routes[0];
                    const fuelToServiceDistance = fuelToServiceRoute.legs[0].distance.value / 1000;
                    const fuelToServiceDuration = fuelToServiceRoute.legs[0].duration.value / 60;

                    // 3. Return route (service to vendor)
                    const returnRenderer = new window.google.maps.DirectionsRenderer({
                      map: mapInstanceRef.current,
                      suppressMarkers: true,
                      polylineOptions: {
                        strokeColor: '#FF9800', // Orange for return
                        strokeWeight: 5,
                        strokeOpacity: 0.7
                      }
                    });

                    directionsService.route(
                      {
                        origin: { lat: memoizedServiceLocation.latitude, lng: memoizedServiceLocation.longitude },
                        destination: { lat: selectedVendor.coordinates.latitude, lng: selectedVendor.coordinates.longitude },
                        travelMode: window.google.maps.TravelMode.DRIVING
                      },
                      (returnResult, returnStatus) => {
                        if (returnStatus === 'OK') {
                          returnRenderer.setDirections(returnResult);
                          returnRouteRef.current = returnRenderer;
                          
                          const returnRoute = returnResult.routes[0];
                          returnDistance = returnRoute.legs[0].distance.value / 1000;

                          // Call the callback with route information
                          if (onRouteCalculated) {
                            onRouteCalculated({
                              vendorId: selectedVendor.location.id,
                              outboundDistance,
                              fuelToServiceDistance,
                              returnDistance,
                              totalDistance: outboundDistance + fuelToServiceDistance + returnDistance,
                              outboundDuration,
                              fuelToServiceDuration,
                              isTowingService: false,
                              hasFuelLocation: true
                            });
                          }
                      }
                    }
                  );
                }
              }
            );
          }
        }
      );
    } else {
        // Two-point routing for regular services without fuel: Vendor → Service → Vendor
      
      // Calculate outbound route (vendor to service location)
      const outboundRenderer = new window.google.maps.DirectionsRenderer({
        map: mapInstanceRef.current,
        suppressMarkers: true,
        polylineOptions: {
          strokeColor: '#4CAF50', // Green for outbound
          strokeWeight: 5,
          strokeOpacity: 0.7
        }
      });

      directionsService.route(
        {
          origin: { lat: selectedVendor.coordinates.latitude, lng: selectedVendor.coordinates.longitude },
          destination: { lat: memoizedServiceLocation.latitude, lng: memoizedServiceLocation.longitude },
          travelMode: window.google.maps.TravelMode.DRIVING
        },
        (result, status) => {
          if (status === 'OK') {
            outboundRenderer.setDirections(result);
            outboundRouteRef.current = outboundRenderer;
            
            const route = result.routes[0];
            outboundDistance = route.legs[0].distance.value / 1000;
            outboundDuration = route.legs[0].duration.value / 60;

            // Calculate return route (service location to vendor)
            const returnRenderer = new window.google.maps.DirectionsRenderer({
              map: mapInstanceRef.current,
              suppressMarkers: true,
              polylineOptions: {
                strokeColor: '#FF9800', // Orange for return
                strokeWeight: 5,
                strokeOpacity: 0.7
              }
            });

            directionsService.route(
              {
                origin: { lat: memoizedServiceLocation.latitude, lng: memoizedServiceLocation.longitude },
                destination: { lat: selectedVendor.coordinates.latitude, lng: selectedVendor.coordinates.longitude },
                travelMode: window.google.maps.TravelMode.DRIVING
              },
              (returnResult, returnStatus) => {
                if (returnStatus === 'OK') {
                  returnRenderer.setDirections(returnResult);
                  returnRouteRef.current = returnRenderer;
                  
                  const returnRoute = returnResult.routes[0];
                  returnDistance = returnRoute.legs[0].distance.value / 1000;

                  // Call the callback with route information
                  if (onRouteCalculated) {
                    onRouteCalculated({
                      vendorId: selectedVendor.location.id,
                      outboundDistance,
                      returnDistance,
                      totalDistance: outboundDistance + returnDistance,
                      outboundDuration,
                        isTowingService: false,
                        hasFuelLocation: false
                      });
                    }
                }
              }
            );
          }
        }
      );
    }
    }
  }, [googleMapsLoaded, memoizedServiceLocation, memoizedDropLocation, selectedVendorId, memoizedVendors, onRouteCalculated, isTowingService, hasFuelLocation, memoizedFuelLocation]);

  // Initialize the map once Google Maps is loaded
  useEffect(() => {
    if (!googleMapsLoaded || !memoizedServiceLocation?.latitude || !memoizedServiceLocation?.longitude) return;

    const { latitude: lat, longitude: lng } = memoizedServiceLocation;
    
    // Create the map instance
    const mapOptions = {
      center: { lat, lng },
      zoom: 12,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
    };
    
    const mapInstance = new window.google.maps.Map(mapContainerRef.current, mapOptions);
    mapInstanceRef.current = mapInstance;

    // Add pickup location marker (red dot)
    // TODO: Migrate to google.maps.marker.AdvancedMarkerElement when ready
    // Current google.maps.Marker is deprecated but still functional
    const serviceMarker = new window.google.maps.Marker({
      position: { lat, lng },
      map: mapInstance,
      icon: {
        url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png', 
        scaledSize: new window.google.maps.Size(40, 40)
      },
      title: 'Pickup Location',
      animation: window.google.maps.Animation.DROP,
    });

    // Add info window for pickup location
    const infoWindow = new window.google.maps.InfoWindow({
      content: `
        <div>
          <strong>Pickup Location</strong><br>
          ${memoizedServiceLocation.address || ''}<br>
          ${memoizedServiceLocation.city || ''}, ${memoizedServiceLocation.state || ''}
        </div>
      `
    });

    serviceMarker.addListener('click', () => {
      infoWindow.open(mapInstance, serviceMarker);
    });

    // Add drop location marker for towing services (purple dot)
    if (isTowingService && memoizedDropLocation?.latitude && memoizedDropLocation?.longitude) {
      const dropMarker = new window.google.maps.Marker({
        position: { lat: memoizedDropLocation.latitude, lng: memoizedDropLocation.longitude },
        map: mapInstance,
        icon: {
          url: 'http://maps.google.com/mapfiles/ms/icons/purple-dot.png', 
          scaledSize: new window.google.maps.Size(40, 40)
        },
        title: 'Drop Location',
        animation: window.google.maps.Animation.DROP,
      });

      // Add info window for drop location
      const dropInfoWindow = new window.google.maps.InfoWindow({
        content: `
          <div>
            <strong>Drop Location</strong><br>
            ${memoizedDropLocation.address || ''}<br>
            ${memoizedDropLocation.city || ''}, ${memoizedDropLocation.state || ''}
          </div>
        `
      });

      dropMarker.addListener('click', () => {
        dropInfoWindow.open(mapInstance, dropMarker);
      });

      dropLocationMarkerRef.current = dropMarker;
    }

    // Add fuel location marker (orange dot)
    if (hasFuelLocation && memoizedFuelLocation?.latitude && memoizedFuelLocation?.longitude) {
      const fuelMarker = new window.google.maps.Marker({
        position: { lat: memoizedFuelLocation.latitude, lng: memoizedFuelLocation.longitude },
        map: mapInstance,
        icon: {
          url: 'http://maps.google.com/mapfiles/ms/icons/orange-dot.png', 
          scaledSize: new window.google.maps.Size(40, 40)
        },
        title: 'Fuel Station',
        animation: window.google.maps.Animation.DROP,
      });

      // Add info window for fuel location
      const fuelInfoWindow = new window.google.maps.InfoWindow({
        content: `
          <div>
            <strong>Fuel Station</strong><br>
            ${memoizedFuelLocation.address || ''}<br>
            ${memoizedFuelLocation.city || ''}, ${memoizedFuelLocation.state || ''}
          </div>
        `
      });

      fuelMarker.addListener('click', () => {
        fuelInfoWindow.open(mapInstance, fuelMarker);
      });

      fuelLocationMarkerRef.current = fuelMarker;
    }

    // Set initial zoom and center
    mapInstance.setZoom(12);
  }, [googleMapsLoaded, memoizedServiceLocation, memoizedDropLocation, isTowingService, hasFuelLocation, memoizedFuelLocation]);

  // Update vendor markers when vendors change or fuel location changes
  useEffect(() => {
    if (!googleMapsLoaded || !mapInstanceRef.current) {
      return;
    }
    
    if (!memoizedVendors || memoizedVendors.length === 0) {
      // Clear existing markers if no vendors
      markersRef.current.forEach(marker => marker.setMap(null));
      markersRef.current = [];
      return;
    }

    // Clear existing vendor markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    // Add vendor markers with a small delay to ensure map is ready
    setTimeout(() => {
      memoizedVendors.forEach(vendorData => {
      const { location, distance_km, operating_hours, coordinates } = vendorData;
      const { vendor, manager_user, location_address } = location;
      
      // Skip if no location data
      if (!coordinates?.latitude || !coordinates?.longitude) return;
      
      const vendorMarker = new window.google.maps.Marker({
        position: { lat: coordinates.latitude, lng: coordinates.longitude },
        map: mapInstanceRef.current,
        icon: {
          url: selectedVendorId === location.id 
            ? 'http://maps.google.com/mapfiles/ms/icons/green-dot.png'
            : 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png', 
          scaledSize: new window.google.maps.Size(30, 30)
        },
        title: `${vendor.vendor_name} (${manager_user.name})`,
      });

      // Add info window for vendor
      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div>
            <strong>${vendor.vendor_name}</strong><br>
            <strong>Manager:</strong> ${manager_user.name}<br>
            <strong>Contact:</strong> ${manager_user.mobile_number}<br>
            <strong>Distance:</strong> ${distance_km} km<br>
            <strong>Status:</strong> ${operating_hours.is_open ? 'Open' : 'Closed'}<br>
            <strong>Address:</strong> ${location_address?.address || 'N/A'}
          </div>
        `
      });

      vendorMarker.addListener('click', () => {
        infoWindow.open(mapInstanceRef.current, vendorMarker);
        if (onVendorSelect) {
          onVendorSelect(location.id);
        }
      });

      markersRef.current.push(vendorMarker);
    });
    }, 100); // Small delay to ensure map is ready

    // Don't calculate bounds here - let the dedicated bounds effect handle it
  }, [googleMapsLoaded, memoizedVendors, selectedVendorId, onVendorSelect, hasFuelLocation, memoizedFuelLocation]);

  // Calculate routes when selected vendor changes or fuel location changes
  useEffect(() => {
    calculateRoutes();
  }, [calculateRoutes, hasFuelLocation, memoizedFuelLocation]);

  // Update bounds when routes are calculated to include all route waypoints
  useEffect(() => {
    if (!googleMapsLoaded || !mapInstanceRef.current) return;

    // Create bounds for the complete route
    const bounds = new window.google.maps.LatLngBounds();
    
    // Add service location
    if (memoizedServiceLocation?.latitude && memoizedServiceLocation?.longitude) {
      bounds.extend({ lat: memoizedServiceLocation.latitude, lng: memoizedServiceLocation.longitude });
    }
    
    // Add fuel location if present (ALWAYS include this)
    if (hasFuelLocation && memoizedFuelLocation?.latitude && memoizedFuelLocation?.longitude) {
      bounds.extend({ lat: memoizedFuelLocation.latitude, lng: memoizedFuelLocation.longitude });
    }
    
    // Add drop location if it's a towing service and different from service location
    if (isTowingService && memoizedDropLocation?.latitude && memoizedDropLocation?.longitude) {
      const serviceLat = memoizedServiceLocation?.latitude;
      const serviceLng = memoizedServiceLocation?.longitude;
      const dropLat = memoizedDropLocation.latitude;
      const dropLng = memoizedDropLocation.longitude;
      
      if (Math.abs(serviceLat - dropLat) > 0.0001 || Math.abs(serviceLng - dropLng) > 0.0001) {
        bounds.extend({ lat: dropLat, lng: dropLng });
      }
    }
    
    // If a vendor is selected, add that vendor and optimize bounds for the route
    if (selectedVendorId) {
      const selectedVendorData = memoizedVendors.find(v => v.location.id === selectedVendorId);
      if (selectedVendorData?.coordinates?.latitude && selectedVendorData?.coordinates?.longitude) {
        bounds.extend({ 
          lat: selectedVendorData.coordinates.latitude, 
          lng: selectedVendorData.coordinates.longitude 
        });
      }
    }
    
    // Add all vendor locations to ensure they're visible for comparison
    memoizedVendors.forEach(vendorData => {
      if (vendorData.coordinates?.latitude && vendorData.coordinates?.longitude) {
        bounds.extend({ 
          lat: vendorData.coordinates.latitude, 
          lng: vendorData.coordinates.longitude 
        });
      }
    });

    // Fit bounds with extra padding to show the complete route
    if (!bounds.isEmpty()) {
      mapInstanceRef.current.fitBounds(bounds, 100);
      
      // Set a minimum zoom level to prevent over-zooming
      setTimeout(() => {
        if (mapInstanceRef.current.getZoom() > 13) {
          mapInstanceRef.current.setZoom(13);
        }
      }, 100);
    }
  }, [googleMapsLoaded, selectedVendorId, memoizedVendors, memoizedServiceLocation, memoizedDropLocation, hasFuelLocation, memoizedFuelLocation, isTowingService]);

  if (googleMapsLoading) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg">
        <div className="text-gray-600">Loading map...</div>
      </div>
    );
  }

  if (googleMapsError) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg">
        <div className="text-red-600">Error loading map: {googleMapsError}</div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Map Legend - Moved outside the map */}
      {memoizedVendors && memoizedVendors.length > 0 && (
        <div className="bg-white p-3 rounded-lg shadow-md text-xs mb-3">
          <div className="flex items-center space-x-4 flex-wrap">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-red-500 rounded-full mr-1"></div>
              <span>{isTowingService ? 'Pickup' : 'Service'}</span>
            </div>
            {hasFuelLocation && (
              <div className="flex items-center">
                <div className="w-3 h-3 bg-orange-500 rounded-full mr-1"></div>
                <span>Fuel Station</span>
              </div>
            )}
            {isTowingService && (
              <div className="flex items-center">
                <div className="w-3 h-3 bg-purple-500 rounded-full mr-1"></div>
                <span>Drop</span>
              </div>
            )}
            <div className="flex items-center">
              <div className="w-3 h-3 bg-blue-500 rounded-full mr-1"></div>
              <span>Vendors</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-1"></div>
              <span>Selected</span>
            </div>
          </div>
          {selectedVendorId && (
            <div className="mt-2 text-xs text-gray-600 border-t pt-2">
              {hasFuelLocation && (
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span>Vendor → Fuel</span>
                </div>
              )}
              {hasFuelLocation ? (
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                  <span>Fuel → {isTowingService ? 'Pickup' : 'Service'}</span>
                </div>
              ) : (
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span>Vendor → {isTowingService ? 'Pickup' : 'Service'}</span>
              </div>
              )}
              {isTowingService && (
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                <span>Pickup → Drop</span>
              </div>
              )}
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                <span>{isTowingService ? 'Drop' : 'Service'} → Vendor</span>
              </div>
            </div>
          )}
        </div>
      )}
      
      <div 
        ref={mapContainerRef} 
        className="w-full h-96 rounded-lg"
        style={{ minHeight: '400px' }}
      />
    </div>
  );
};

export default Map; 